import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { 
  Gauge, CircleDot, Droplets, Settings, Thermometer, Wrench, 
  Battery, Lightbulb, Car, Fuel, ArrowRight, CheckCircle2, Phone 
} from 'lucide-react';

const services = [
  {
    icon: Gauge,
    title: 'Engine Diagnostics',
    description: 'State-of-the-art computer diagnostics to identify engine issues with precision. Our advanced equipment reads all error codes and sensor data.',
    features: ['Check engine light diagnosis', 'Performance analysis', 'Emissions testing', 'Computer system scanning'],
    price: 'From $89',
  },
  {
    icon: CircleDot,
    title: 'Brake Services',
    description: 'Complete brake system inspection, maintenance, and repair. Your safety is our top priority with quality parts and expert service.',
    features: ['Brake pad replacement', 'Rotor resurfacing', 'Brake fluid flush', 'ABS system repair'],
    price: 'From $149',
  },
  {
    icon: Droplets,
    title: 'Oil Changes',
    description: 'Premium synthetic and conventional oil changes with comprehensive multi-point vehicle inspection included.',
    features: ['Synthetic & conventional oils', 'Filter replacement', 'Fluid top-off', 'Multi-point inspection'],
    price: 'From $39',
  },
  {
    icon: Settings,
    title: 'Tire Services',
    description: 'Complete tire care including rotation, balancing, alignment, and new tire installation from top brands.',
    features: ['Tire rotation', 'Wheel balancing', 'Alignment check', 'New tire installation'],
    price: 'From $29',
  },
  {
    icon: Thermometer,
    title: 'AC & Heating',
    description: 'Keep comfortable year-round with our climate control system services, from repairs to refrigerant recharge.',
    features: ['AC recharge', 'Compressor repair', 'Heater core service', 'Climate control diagnosis'],
    price: 'From $79',
  },
  {
    icon: Wrench,
    title: 'General Repairs',
    description: 'From minor fixes to major repairs, our certified technicians handle all makes and models with expertise.',
    features: ['Suspension work', 'Steering repairs', 'Exhaust system', 'Engine repairs'],
    price: 'Varies',
  },
  {
    icon: Battery,
    title: 'Battery Services',
    description: 'Battery testing, charging system diagnosis, and replacement with quality batteries backed by warranty.',
    features: ['Battery testing', 'Alternator check', 'Starter diagnosis', 'New battery installation'],
    price: 'From $29',
  },
  {
    icon: Lightbulb,
    title: 'Electrical Systems',
    description: 'Expert diagnosis and repair of all vehicle electrical systems, from simple fixes to complex wiring.',
    features: ['Lighting repairs', 'Wiring diagnosis', 'Sensor replacement', 'Module programming'],
    price: 'From $79',
  },
  {
    icon: Car,
    title: 'Transmission Service',
    description: 'Transmission fluid changes, repairs, and rebuilds to keep your drivetrain running smoothly.',
    features: ['Fluid exchange', 'Filter replacement', 'Clutch repair', 'Transmission rebuild'],
    price: 'From $149',
  },
  {
    icon: Fuel,
    title: 'Fuel System',
    description: 'Improve performance and efficiency with our fuel system cleaning and repair services.',
    features: ['Injector cleaning', 'Fuel pump service', 'Filter replacement', 'System pressure test'],
    price: 'From $99',
  },
];

export default function Services() {
  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&w=2000&q=80"
            alt="Auto service"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-900" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block px-4 py-1.5 bg-amber-500/10 text-amber-400 text-sm font-semibold rounded-full mb-4"
          >
            Our Services
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-bold text-white mb-6"
          >
            Complete Auto Care Solutions
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-slate-400 max-w-2xl mx-auto"
          >
            From routine maintenance to complex repairs, our certified technicians deliver excellence for every vehicle.
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, i) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="group bg-slate-50 hover:bg-slate-900 rounded-2xl p-8 transition-all duration-500"
              >
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 bg-amber-100 group-hover:bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-500">
                    <service.icon className="w-7 h-7 text-amber-600 group-hover:text-slate-900 transition-colors duration-500" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-xl font-semibold text-slate-900 group-hover:text-white transition-colors duration-500">
                        {service.title}
                      </h3>
                      <span className="text-amber-600 group-hover:text-amber-400 font-semibold text-sm transition-colors duration-500">
                        {service.price}
                      </span>
                    </div>
                    <p className="text-slate-600 group-hover:text-slate-400 text-sm mb-4 transition-colors duration-500">
                      {service.description}
                    </p>
                    <ul className="space-y-2">
                      {service.features.map((feature) => (
                        <li key={feature} className="flex items-center gap-2 text-sm text-slate-500 group-hover:text-slate-400 transition-colors duration-500">
                          <CheckCircle2 className="w-4 h-4 text-amber-500 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-1.5 bg-amber-100 text-amber-700 text-sm font-semibold rounded-full mb-4">
              How It Works
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Simple, Transparent Process
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Getting your vehicle serviced has never been easier. Here's what to expect.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Book Online', description: 'Schedule your appointment through our website or give us a call.' },
              { step: '02', title: 'Drop Off', description: 'Bring your vehicle in at your scheduled time. We handle the rest.' },
              { step: '03', title: 'Get Updates', description: 'Receive real-time updates and approval requests before any work.' },
              { step: '04', title: 'Pick Up', description: 'Collect your serviced vehicle and review our detailed report.' },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative"
              >
                {i < 3 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-amber-400 to-transparent z-0" />
                )}
                <div className="relative z-10">
                  <span className="text-5xl font-bold text-amber-400/30">{item.step}</span>
                  <h3 className="text-xl font-semibold text-slate-900 mt-2 mb-2">{item.title}</h3>
                  <p className="text-slate-600">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Schedule Your Service?
            </h2>
            <p className="text-lg text-slate-400 mb-8">
              Our team is ready to provide your vehicle with the expert care it deserves. Book your appointment today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to={createPageUrl('Contact')}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-500 text-slate-900 font-semibold rounded-xl hover:from-amber-300 hover:to-amber-400 transition-all duration-300 shadow-xl shadow-amber-500/25 group"
              >
                Book Appointment
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="tel:5551234567"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-all duration-300 backdrop-blur-sm border border-white/10"
              >
                <Phone className="w-5 h-5" />
                (555) 123-4567
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}